import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import Button from '../ui/Button.jsx';
import TerminalWindow from '../primitives/TerminalWindow.jsx';
import PromptEyebrow from '../ui/PromptEyebrow.jsx';
import useInView from '../../hooks/useInView.js';
import useReveal from '../../hooks/useReveal.js';
import {
  forWhoHead,
  forWhoMeter,
  forWhoFoot,
  forWhoCards,
} from '../../content/forWho.js';

/**
 * ForWho — "Cost Leak Meter" section.
 *
 * Top: live ticker counting burned dollars at an accelerated rate.
 * Middle: three persona "slabs" with before→after hero numbers.
 * Each slab's title types out on reveal and on hover; numbers roll from
 * zero on first reveal and re-roll on every hover.
 * Foot: single CTA — plain router link to the /intake page.
 */

/* ---------- animation helpers ---------- */

/** Rolls a numeric value from `start` → `target`. Re-runs on `trigger` change. */
function useRollNumber(target, trigger, { duration = 900, start = 0 } = {}) {
  const [value, setValue] = useState(start);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // cubic ease-out
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setValue(start + (target - start) * ease(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, trigger, duration]);
  return value;
}

/** Types `full` left-to-right while `active`. Snaps back to `rest` on deactivate. */
function useTypeOut(full, rest, active) {
  const [text, setText] = useState(rest);
  useEffect(() => {
    if (!active) {
      setText(rest);
      return;
    }
    let raf;
    const stepDur = 18; // ms/char
    const t0 = performance.now();
    const tick = (t) => {
      const n = Math.min(full.length, Math.floor((t - t0) / stepDur));
      setText(full.slice(0, n));
      if (n < full.length) raf = requestAnimationFrame(tick);
      else setText(full);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, full, rest]);
  return text;
}

/* ---------- sub-components ---------- */

function ArrowIcon() {
  return (
    <span className="fwa-arrow" aria-hidden="true">
      <span className="fwa-arrow-track" />
      <span className="fwa-arrow-head" />
    </span>
  );
}

/**
 * Splits the heading around an `*emphasised*` phrase so the emphasised
 * run can receive the purple→blue gradient fill without us having to
 * hand-author HTML in content files.
 */
function LedeHeading({ heading }) {
  const match = heading.match(/^(.*?)\*(.+?)\*(.*)$/);
  if (!match) return <h2>{heading}</h2>;
  const [, pre, em, post] = match;
  return (
    <h2>
      {pre}
      <em>{em}</em>
      {post}
    </h2>
  );
}

function Slab({ card, index }) {
  const [slabRef, slabVisible] = useInView({
    rootMargin: '0px 0px -80px 0px',
    threshold: 0.15,
  });
  const [slabIn, setSlabIn] = useState(false);
  const [hot, setHot] = useState(false);
  const [hoverKey, setHoverKey] = useState(0); // bumped on enter to re-roll

  // Entrance + number roll start when the slab scrolls into view. The
  // small index offset cascades slabs that enter the viewport together.
  useEffect(() => {
    if (!slabVisible) return;
    const delay = 90 + index * 60;
    const t = setTimeout(() => setSlabIn(true), delay);
    return () => clearTimeout(t);
  }, [slabVisible, index]);

  const rollActive = slabIn || hot;
  const rollTrigger = rollActive ? `on-${hoverKey}` : 'off';

  const beforeVal = useRollNumber(
    rollActive ? card.beforeNum || 0 : 0,
    rollTrigger,
    { duration: 1100 }
  );
  const afterVal = useRollNumber(
    rollActive ? card.afterNum || 0 : 0,
    rollTrigger,
    { duration: 1400 }
  );

  const typedTitle = useTypeOut(card.trigger, card.restBody, hot || slabIn);
  const typedLabelBefore = useTypeOut(
    card.labelBefore || '',
    card.labelRest || card.labelBefore || '',
    hot || slabIn
  );

  const handleEnter = () => {
    setHot(true);
    setHoverKey((k) => k + 1);
  };
  const handleLeave = () => setHot(false);

  const fmt = (v) => Math.round(v).toString();
  const isLabel = card.kind === 'label-percent';
  const isText = card.kind === 'currency-to-text';

  const figureAria = isLabel
    ? `${card.labelRest || card.labelBefore} ${card.beforeNum}${card.beforeUnit} to ${card.labelAfter} ${card.afterNum}${card.afterUnit}`
    : `${card.beforeLabel || ''}${card.beforeNum}${card.beforeUnit || ''} to ${
        isText ? card.afterText : `${card.afterLabel || ''}${card.afterNum}${card.afterUnit || ''}`
      }`;

  return (
    <div
      ref={slabRef}
      className={`fwa-slab${slabIn ? ' is-in' : ''}${hot ? ' is-hot' : ''}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      tabIndex={0}
    >
      <div className="fwa-num">
        <span className="fwa-idx">0{index + 1} · Leak signature</span>

        <div className="fwa-figure" aria-label={figureAria}>
          <span className="fwa-fig-before">
            {isLabel ? (
              <>
                <span className="fwa-fig-label">{typedLabelBefore}</span>
                <span className="fwa-fig-dot"> · </span>
                {fmt(beforeVal)}
                {card.beforeUnit}
              </>
            ) : (
              <>
                {card.beforeLabel}
                {fmt(beforeVal)}
                {card.beforeUnit}
              </>
            )}
          </span>

          <ArrowIcon />

          <span className="fwa-fig-after">
            {isText ? (
              card.afterText
            ) : isLabel ? (
              <>
                <span className="fwa-fig-label">{card.labelAfter}</span>
                <span className="fwa-fig-dot"> · </span>
                {fmt(afterVal)}
                {card.afterUnit}
              </>
            ) : (
              <>
                {card.afterLabel}
                {fmt(afterVal)}
                {card.afterUnit}
              </>
            )}
          </span>
        </div>

        <span className="fwa-figure-sub">
          {card.subBefore} · {card.subAfter}
        </span>
      </div>

      <div className="fwa-body">
        <PromptEyebrow caret={false}>For · {card.title}</PromptEyebrow>
        <h3 className="fwa-title" aria-label={card.trigger}>
          <span className="fwa-typed">{typedTitle}</span>
          <span className="fwa-caret" aria-hidden="true" />
        </h3>
        <p className="fwa-say">
          <strong>If this is you: </strong>
          {card.body}
        </p>
      </div>
    </div>
  );
}

/* ---------- section ---------- */

export default function ForWho() {
  // Each block gets its own viewport trigger. The section is several
  // screens tall, so gating on the root would fire everything the moment
  // the section's top edge clips the fold — finished before it's seen.
  const [meterRef, meterIn] = useInView({
    rootMargin: '0px 0px -60px 0px',
    threshold: 0.2,
  });
  const ledeRef = useReveal();
  const footRef = useReveal();

  // Live ticker — dollars/sec = annualRate * speedup.
  const [leaked, setLeaked] = useState(0);
  useEffect(() => {
    if (!meterIn) return;
    let raf;
    const start = performance.now();
    const base = leaked;
    const ratePerSec =
      (forWhoMeter.annualDollars / (365 * 24 * 60 * 60)) * forWhoMeter.speedup;
    const tick = (t) => {
      const dt = (t - start) / 1000;
      setLeaked(base + dt * ratePerSec);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterIn]);

  const fmtDollars = (n) => '$' + Math.floor(n).toLocaleString('en-US');

  return (
    <section
      id="for-who"
      className="fwa-root bg-surface text-ink-50 border-t border-white/[.06]"
    >
      <div className="container-page">
        <div
          ref={meterRef}
          className={`fwa-terminal${meterIn ? ' is-in' : ''}`}
          role="status"
          aria-live="polite"
        >
          <TerminalWindow>
            <div className="flex flex-col gap-3">
              <span className="font-mono text-mono-xs uppercase tracking-[0.14em] text-ink-300">
                {forWhoMeter.label}
              </span>
              <div className="flex items-baseline gap-3">
                <span className="font-display font-extrabold text-stat tabular-nums text-white">
                  {fmtDollars(leaked)}
                </span>
                <span className="font-mono text-mono-xs uppercase tracking-[0.14em] text-ink-300">
                  {forWhoMeter.unit}
                </span>
              </div>
              <span className="font-mono text-mono-xs text-ink-400">
                {forWhoMeter.note}
              </span>
            </div>
          </TerminalWindow>
        </div>

        <div ref={ledeRef} className="fwa-lede">
          <PromptEyebrow>{forWhoHead.eyebrow}</PromptEyebrow>
          <LedeHeading heading={forWhoHead.heading} />
          <p>{forWhoHead.description}</p>
        </div>

        {forWhoCards.map((c, i) => (
          <Slab key={c.id} card={c} index={i} />
        ))}

        <div ref={footRef} className="fwa-foot">
          <span className="fwa-foot-text">{forWhoFoot.line}</span>
          <Button as={Link} to="/intake">
            {forWhoFoot.button}
          </Button>
        </div>
      </div>
    </section>
  );
}
